/* v05_a24-dawn-refactor/assets/a24-collection-filters.js */
/**
 * Native Shopify / Search & Discovery filter UI.
 * Product results, counts and pagination come from Liquid/Shopify.
 * PS range uses hidden native Shopify value filters so it remains compatible
 * with Search & Discovery, shared URLs and the no-JS GET fallback.
 */
class CollectionFilters extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;

    this.form = this.querySelector('form');
    this.minRange = this.querySelector('[data-price-min]');
    this.maxRange = this.querySelector('[data-price-max]');
    this.minLabel = this.querySelector('[data-price-min-label]');
    this.maxLabel = this.querySelector('[data-price-max-label]');
    this.currencySymbol = this.dataset.currency || '€';
    this.applyButtons = Array.from(this.querySelectorAll('[data-apply-filters]'));
    this.applyButton = this.applyButtons[0] || null;
    this.filtersPanel = this.closest('.filters-panel') || document.getElementById('main-collection-filters');
    this.filterOverlay = document.querySelector('[data-filter-overlay]');
    this.filterToggles = Array.from(document.querySelectorAll('[data-filter-toggle]'));
    this.filterClose = this.filtersPanel ? this.filtersPanel.querySelector('[data-filter-close]') : null;
    this.config = this.normalizeConfig(window.A24?.collectionFilters || {});
    this.pendingAutoApplyTimer = null;
    this.pendingPreviewRefreshTimer = null;
    this.previewRefreshAbortController = null;
    this.vendorPriceBoundsWarmupPromise = null;
    this.vendorPriceBoundsWarmupDone = false;
    this.vendorPriceBoundsAbortController = null;
    this.vendorPriceBoundsWarmupRequestId = 0;
    if (typeof window.A24PsFilterRange !== 'function') {
      console.error('a24-collection-filters-ps-filter-range.js wurde nicht vor a24-collection-filters.js geladen.');
      return;
    }

    this.psRange = new window.A24PsFilterRange(this);

    this.boundListeners = [];
    this.priceBoundsAbortController = null;
    this.priceBoundsRequestId = 0;

    this.handleRangeChange = this.handleRangeChange.bind(this);
    this.handleApply = this.handleApply.bind(this);
    this.handleFormSubmit = this.handleFormSubmit.bind(this);
    this.openFilters = this.openFilters.bind(this);
    this.closeFilters = this.closeFilters.bind(this);
    this.handleEscapeKey = this.handleEscapeKey.bind(this);
    this.handleViewportChange = this.handleViewportChange.bind(this);

    this.addListener(this.minRange, 'input', this.handleRangeChange);
    this.addListener(this.maxRange, 'input', this.handleRangeChange);
    this.addListener(this.minRange, 'change', this.handleRangeChange);
    this.addListener(this.maxRange, 'change', this.handleRangeChange);
    this.applyButtons.forEach((button) => {
      this.addListener(button, 'click', this.handleApply);
    });
    /*
     * Handle the submit in the capture phase so a PS change on /search is
     * redirected to /collections/all before any legacy or third-party
     * submit listener can navigate back to the search endpoint.
     */
    this.addListener(this.form, 'submit', this.handleFormSubmit, true);

    this.attachSelectionHandlers();
    this.attachSearchFilterResetHandlers();
    this.attachFilterPanelHandlers();
    this.attachFilterBlockCollapseHandlers();
    this.syncApplyModeUi();
    this.applyPriceOnRequestVendorRule();
    this.initializePriceInputs();
    this.psRange.init();
    this.sortActiveFilterChips();
    this.handleRangeChange();
    this.updatePriceSingleValueState();
    const previewBaseUrl = this.dataset.previewBaseUrl || '';
    // Alle Preisabfragen (Warmup + Vollscan) laufen bewusst gegen dieselbe
    // Filter-URL, damit kein Mischen von alter/newer Selection entsteht.
    const priceBoundsContextUrl = this.resolvePriceBoundsContextUrl(previewBaseUrl || null);
    this.buildVendorPriceBoundsCacheFromCurrentDom();
    this.vendorPriceBoundsWarmupPromise = this.warmVendorPriceBoundsCacheFromAllPages(priceBoundsContextUrl);
    if (!this.isPriceOnRequestVendorSelection()) {
      if (this.hasSingleResultsPage()) {
        this.applyVisibleGridPriceBoundsFromCurrentDom();
      }
      this.applyVendorPriceBoundsFromDomCache();
      this.refreshPriceBoundsFromFilteredProducts(priceBoundsContextUrl);
    }
  }

  disconnectedCallback() {
    this.removeAllListeners();
    this.clearPendingPreviewRefresh();
    if (this.previewRefreshAbortController) this.previewRefreshAbortController.abort();
    this.previewRefreshAbortController = null;
    if (this.priceBoundsAbortController) this.priceBoundsAbortController.abort();
    this.priceBoundsAbortController = null;
    if (this.vendorPriceBoundsAbortController) this.vendorPriceBoundsAbortController.abort();
    this.vendorPriceBoundsAbortController = null;
    this.vendorPriceBoundsWarmupRequestId += 1;
    this._initialized = false;
  }

  resolvePriceBoundsContextUrl(baseUrl = null) {
    const url = baseUrl
      ? new URL(baseUrl, window.location.origin)
      : new URL(window.location.href);

    // section_id/page variieren je Preview/Pagination und dürfen nicht Teil
    // des stabilen Preis-Kontexts sein.
    url.searchParams.delete('section_id');
    url.searchParams.delete('page');
    return url;
  }

  hasSingleResultsPage() {
    const grid = document.querySelector('.collection-section .product-grid[data-total-pages]') ||
      document.querySelector('.product-grid[data-total-pages]');
    if (!grid) return true;
    const totalPages = Math.max(1, Number.parseInt(grid.dataset.totalPages || '1', 10) || 1);
    return totalPages <= 1;
  }

  addListener(target, eventName, handler, options) {
    if (!target || !eventName || !handler) return;

    const guardedHandler = (event) => {
      if (
        eventName === 'click' &&
        window.A24Theme?.shouldProtectEditorClicks?.()
      ) {
        return;
      }

      handler(event);
    };

    target.addEventListener(eventName, guardedHandler, options);
    this.boundListeners.push({ target, eventName, handler: guardedHandler, options });
  }

  removeAllListeners() {
    this.boundListeners.forEach(({ target, eventName, handler, options }) => {
      target.removeEventListener(eventName, handler, options);
    });
    this.boundListeners = [];
    this.clearPendingAutoApply();
  }

  normalizeConfig(config) {
    const applyMode = ['manual', 'auto'].includes(config.applyMode) ? config.applyMode : 'manual';
    const psInputRequirement = ['one', 'both', 'smart'].includes(config.psInputRequirement)
      ? config.psInputRequirement
      : 'one';

    return {
      applyMode,
      debounceMs: this.clampInteger(config.debounceMs, 200, 2000, 700),
      psInputRequirement,
      psSecondFieldWaitMs: this.clampInteger(config.psSecondFieldWaitMs, 300, 3000, 1200),
      priceOnRequestVendors: this.normalizePriceOnRequestVendors(config.priceOnRequestVendors)
    };
  }

  normalizePriceOnRequestVendors(configuredVendors) {
    const vendors = [];

    const pushVendor = (value) => {
      const normalized = this.normalizeVendorValue(value);
      if (!normalized || vendors.includes(normalized)) return;
      vendors.push(normalized);
    };

    if (Array.isArray(configuredVendors)) {
      configuredVendors.forEach((value) => pushVendor(value));
      return vendors;
    }

    if (configuredVendors && typeof configuredVendors === 'object') {
      String(configuredVendors.list || configuredVendors.custom || configuredVendors.vendors || '')
        .split(',')
        .forEach((value) => pushVendor(value));

      return vendors;
    }

    String(configuredVendors || '')
      .split(',')
      .forEach((value) => pushVendor(value));

    return vendors;
  }

  normalizeVendorValue(value) {
    return String(value || '').trim().toLowerCase();
  }

  isConfiguredPriceOnRequestVendor(vendorName) {
    const normalizedVendor = this.normalizeVendorValue(vendorName);
    if (!normalizedVendor) return false;

    return this.config.priceOnRequestVendors.some((configuredVendor) => {
      return normalizedVendor === configuredVendor ||
        normalizedVendor.includes(configuredVendor) ||
        configuredVendor.includes(normalizedVendor);
    });
  }

  getSelectedVendorValues() {
    return Array.from(this.querySelectorAll('input[name="filter.p.vendor"]:checked'))
      .map((input) => this.normalizeVendorValue(input.value))
      .filter(Boolean);
  }

  getAvailableVendorValuesFromFilterPanel() {
    const vendorInputs = Array.from(this.querySelectorAll('input[name="filter.p.vendor"]'));
    if (!vendorInputs.length) return [];

    return vendorInputs
      .filter((input) => input.checked || !input.disabled)
      .map((input) => this.normalizeVendorValue(input.value))
      .filter(Boolean);
  }

  getVisibleProductCardVendors() {
    const vendors = Array.from(document.querySelectorAll('.product-card__brand'))
      .map((element) => this.normalizeVendorValue(element.textContent))
      .filter(Boolean);

    return Array.from(new Set(vendors));
  }

  buildVendorPriceBoundsCacheFromCurrentDom() {
    this.vendorPriceBoundsCache = Object.create(null);

    const grid = document.querySelector('.collection-section .product-grid') || document.querySelector('.product-grid');
    if (!grid) return;

    Array.from(grid.querySelectorAll('.product-card')).forEach((card) => {
      this.mergeVendorBoundsMap(
        this.vendorPriceBoundsCache,
        this.extractVendorBoundsFromProductCard(card)
      );
    });

    this.syncVendorPriceBoundsToInputs();
  }

  async warmVendorPriceBoundsCacheFromAllPages(baseUrl = null) {
    const grid = document.querySelector('.collection-section .product-grid[data-total-pages]') ||
      document.querySelector('.product-grid[data-total-pages]');
    if (!grid) {
      this.vendorPriceBoundsWarmupDone = true;
      return;
    }

    const totalPages = Math.max(1, Number.parseInt(grid.dataset.totalPages || '1', 10) || 1);
    if (totalPages <= 1) {
      this.vendorPriceBoundsWarmupDone = true;
      return;
    }

    const requestId = ++this.vendorPriceBoundsWarmupRequestId;
    const resolvedBaseUrl = this.resolvePriceBoundsContextUrl(baseUrl);
    this.vendorPriceBoundsWarmupDone = false;

    if (this.vendorPriceBoundsAbortController) this.vendorPriceBoundsAbortController.abort();
    this.vendorPriceBoundsAbortController = new AbortController();
    const signal = this.vendorPriceBoundsAbortController.signal;

    for (let pageNumber = 2; pageNumber <= totalPages; pageNumber += 1) {
      try {
        const pageData = await this.fetchFilteredProductPageData(pageNumber, signal, resolvedBaseUrl);
        this.mergeVendorBoundsMap(this.vendorPriceBoundsCache, pageData.vendorBounds || {});
      } catch (error) {
        if (error && error.name === 'AbortError') return;
        break;
      }
    }

    if (requestId !== this.vendorPriceBoundsWarmupRequestId || signal.aborted) return;

    this.vendorPriceBoundsWarmupDone = true;
    this.syncVendorPriceBoundsToInputs();
    this.applyVendorPriceBoundsFromDomCache();
  }

  extractVendorBoundsFromProductCard(card) {
    const vendorName = this.normalizeVendorValue(card.querySelector('.product-card__brand')?.textContent);
    if (!vendorName || this.isConfiguredPriceOnRequestVendor(vendorName)) {
      return null;
    }

    const requestLabel = card.querySelector('.product-card__price-label');
    if (requestLabel && String(requestLabel.textContent || '').toLowerCase().includes('preis auf anfrage')) {
      return null;
    }

    const value = this.parseMoney(card.querySelector('.product-card__price-current')?.textContent);
    if (!Number.isFinite(value)) {
      return null;
    }

    const map = Object.create(null);
    map[vendorName] = { min: value, max: value };
    return map;
  }

  mergeVendorBoundsMap(targetMap, sourceMap) {
    if (!sourceMap) return;

    Object.keys(sourceMap).forEach((vendorName) => {
      const next = sourceMap[vendorName];
      if (!next) return;

      const existing = targetMap[vendorName];
      if (!existing) {
        targetMap[vendorName] = { min: next.min, max: next.max };
        return;
      }

      existing.min = Math.min(existing.min, next.min);
      existing.max = Math.max(existing.max, next.max);
    });
  }

  syncVendorPriceBoundsToInputs() {
    if (!this.vendorPriceBoundsCache) return;

    this.querySelectorAll('input[name="filter.p.vendor"]').forEach((input) => {
      const vendorName = this.normalizeVendorValue(input.value);
      const bounds = this.vendorPriceBoundsCache[vendorName];

      if (!bounds) {
        input.removeAttribute('data-vendor-price-min');
        input.removeAttribute('data-vendor-price-max');
        return;
      }

      input.dataset.vendorPriceMin = String(bounds.min);
      input.dataset.vendorPriceMax = String(bounds.max);
    });
  }

  applyVendorPriceBoundsFromDomCache() {
    if (!this.minRange || !this.maxRange || this.isPriceOnRequestVendorSelection()) return;

    const vendorInputs = Array.from(this.querySelectorAll('input[name="filter.p.vendor"]'));
    if (!vendorInputs.length) return;

    const selectedInputs = vendorInputs.filter((input) => input.checked);
    const sourceInputs = selectedInputs.length ? selectedInputs : vendorInputs;

    if (!this.vendorPriceBoundsWarmupDone && selectedInputs.length) {
      return;
    }

    const minValues = [];
    const maxValues = [];

    sourceInputs.forEach((input) => {
      const min = this.parseNumber(input.dataset.vendorPriceMin);
      const max = this.parseNumber(input.dataset.vendorPriceMax);
      if (!Number.isFinite(min) || !Number.isFinite(max)) return;
      minValues.push(min);
      maxValues.push(max);
    });

    if (!minValues.length || !maxValues.length) return;

    this.applyFilteredPriceBounds(Math.min(...minValues), Math.max(...maxValues));
  }

  getPriceValuesFromProductCards(cards) {
    return Array.from(cards || [])
      .filter((card) => {
        const requestLabel = card.querySelector('.product-card__price-label');
        if (requestLabel && String(requestLabel.textContent || '').toLowerCase().includes('preis auf anfrage')) {
          return false;
        }

        const vendorName = this.normalizeVendorValue(card.querySelector('.product-card__brand')?.textContent);
        if (vendorName && this.isConfiguredPriceOnRequestVendor(vendorName)) {
          return false;
        }

        return true;
      })
      .map((card) => this.parseMoney(card.querySelector('.product-card__price-current')?.textContent))
      .filter((value) => Number.isFinite(value));
  }

  applyVisibleGridPriceBoundsFromCurrentDom() {
    const grid = document.querySelector('.collection-section .product-grid') || document.querySelector('.product-grid');
    if (!grid) return;

    const prices = this.getPriceValuesFromProductCards(grid.querySelectorAll('.product-card'));
    if (!prices.length) return;

    this.applyFilteredPriceBounds(Math.min(...prices), Math.max(...prices));
  }

  shouldHidePriceForCurrentVendorContext() {
    if (!this.config.priceOnRequestVendors.length) return false;

    const selectedVendors = this.getSelectedVendorValues();
    if (selectedVendors.length) {
      return selectedVendors.every((vendor) => this.isConfiguredPriceOnRequestVendor(vendor));
    }

    const availableVendors = this.getAvailableVendorValuesFromFilterPanel();
    if (availableVendors.length) {
      return availableVendors.every((vendor) => this.isConfiguredPriceOnRequestVendor(vendor));
    }

    const visibleVendors = this.getVisibleProductCardVendors();
    if (visibleVendors.length) {
      return visibleVendors.every((vendor) => this.isConfiguredPriceOnRequestVendor(vendor));
    }

    return false;
  }

  clampInteger(value, min, max, fallback) {
    const number = Number.parseInt(value, 10);
    if (!Number.isFinite(number)) return fallback;
    return Math.min(max, Math.max(min, number));
  }

  isAutoApplyEnabled() {
    return this.config.applyMode === 'auto';
  }

  syncApplyModeUi() {
    this.dataset.applyMode = this.config.applyMode;
    if (!this.applyButtons.length) return;

    const shouldHideButton = this.isAutoApplyEnabled();
    this.applyButtons.forEach((button) => {
      button.hidden = shouldHideButton;
      button.setAttribute('aria-hidden', shouldHideButton ? 'true' : 'false');
    });
  }

  clearPendingAutoApply() {
    if (!this.pendingAutoApplyTimer) return;
    window.clearTimeout(this.pendingAutoApplyTimer);
    this.pendingAutoApplyTimer = null;
  }

  clearPendingPreviewRefresh() {
    if (!this.pendingPreviewRefreshTimer) return;
    window.clearTimeout(this.pendingPreviewRefreshTimer);
    this.pendingPreviewRefreshTimer = null;
  }

  queuePreviewRefresh(context = {}) {
    if (this.isAutoApplyEnabled() || !this.form) return;
    if (window.A24Theme?.shouldProtectEditorClicks?.()) return;
    if (context.source !== 'selection') return;

    this.clearPendingPreviewRefresh();
    this.pendingPreviewRefreshTimer = window.setTimeout(() => {
      this.pendingPreviewRefreshTimer = null;
      this.refreshFilterFieldsPreview();
    }, 260);
  }

  setFiltersLoadingState(isLoading) {
    const panel = document.getElementById('main-collection-filters');
    if (!panel) return;

    panel.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    const indicator = panel.querySelector('[data-filters-loading-indicator]');
    if (indicator) indicator.hidden = !isLoading;
  }

  resolvePreviewSectionId() {
    const sectionRoot = document.querySelector('.collection-section[data-section-id]');
    return sectionRoot ? String(sectionRoot.dataset.sectionId || '').trim() : '';
  }

  buildPreviewUrl(sectionId) {
    const url = this.buildPreviewBaseUrl();
    url.searchParams.set('section_id', sectionId);
    return url;
  }

  buildPreviewBaseUrl() {
    const action = this.form?.getAttribute('action') || window.location.pathname;
    const url = new URL(action, window.location.origin);
    const params = new URLSearchParams(new FormData(this.form));
    params.delete('page');

    params.forEach((value, key) => {
      url.searchParams.append(key, value);
    });

    return url;
  }

  async refreshFilterFieldsPreview() {
    if (!this.form || this.isAutoApplyEnabled()) return;

    const sectionId = this.resolvePreviewSectionId();
    if (!sectionId) return;

    this.syncPriceFilterParams();

    if (this.previewRefreshAbortController) this.previewRefreshAbortController.abort();
    this.previewRefreshAbortController = new AbortController();
    const signal = this.previewRefreshAbortController.signal;

    this.setFiltersLoadingState(true);

    try {
      const response = await fetch(this.buildPreviewUrl(sectionId).toString(), {
        credentials: 'same-origin',
        signal,
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) {
        throw new Error('Filter-Preview fehlgeschlagen: HTTP ' + response.status);
      }

      const html = await response.text();
      if (signal.aborted) return;

      const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
      const incomingPanel = parsedDocument.getElementById('main-collection-filters');
      const currentPanel = document.getElementById('main-collection-filters');
      if (!incomingPanel || !currentPanel) return;

      const wasOpen = currentPanel.classList.contains('is-open');
      const overlay = document.querySelector('[data-filter-overlay]');
      const currentChips = currentPanel.querySelector('.active-filter-chips');
      const incomingChips = incomingPanel.querySelector('.active-filter-chips');
      const currentResetLink = currentPanel.querySelector('[data-reset-filters]');
      const incomingResetLink = incomingPanel.querySelector('[data-reset-filters]');
      const previewBaseUrl = this.buildPreviewBaseUrl();
      // Serverantworten können kurz veraltete range_max-Werte enthalten.
      // Deshalb behalten wir den letzten validen Preiszustand lokal bei.
      const preservedPriceState = this.capturePriceUiState(currentPanel);

      if (currentChips) {
        if (incomingChips) {
          incomingChips.replaceWith(currentChips.cloneNode(true));
        } else {
          const header = incomingPanel.querySelector('.filters-panel__header');
          if (header) {
            header.insertAdjacentElement('afterend', currentChips.cloneNode(true));
          }
        }
      } else if (incomingChips) {
        incomingChips.remove();
      }

      if (currentResetLink) {
        if (incomingResetLink) {
          incomingResetLink.replaceWith(currentResetLink.cloneNode(true));
        }
      } else if (incomingResetLink) {
        incomingResetLink.remove();
      }

      const incomingFilters = incomingPanel.querySelector('collection-filters');
      if (incomingFilters) {
        incomingFilters.dataset.previewBaseUrl = previewBaseUrl.toString();
      }

      if (wasOpen) {
        incomingPanel.classList.add('is-open');
      }

      // Der Preiszustand wird vor dem Replace wiederhergestellt, damit der
      // Nutzer keinen 200k+-Zwischenwert beim Panel-Tausch sieht.
      this.restorePriceUiState(incomingPanel, preservedPriceState);

      currentPanel.replaceWith(incomingPanel);

      if (overlay) {
        overlay.classList.toggle('is-active', wasOpen);
      }

      document.body.classList.toggle('filters-open', wasOpen);
      window.dispatchEvent(new Event('scroll'));
    } catch (error) {
      if (error && error.name === 'AbortError') return;
      console.warn('Filteroptionen konnten nicht live aktualisiert werden.', error);
    } finally {
      this.setFiltersLoadingState(false);
    }
  }

  capturePriceUiState(panel) {
    if (!panel) return null;

    const minInput = panel.querySelector('[data-price-min]');
    const maxInput = panel.querySelector('[data-price-max]');
    const minLabel = panel.querySelector('[data-price-min-label]');
    const maxLabel = panel.querySelector('[data-price-max-label]');
    const priceBlock = minInput ? minInput.closest('.filter-block') : null;
    if (!minInput || !maxInput) return null;

    return {
      min: {
        min: minInput.min,
        max: minInput.max,
        value: minInput.value,
        name: minInput.getAttribute('name') || '',
        paramName: minInput.dataset.priceParamName || '',
        disabled: minInput.disabled
      },
      max: {
        min: maxInput.min,
        max: maxInput.max,
        value: maxInput.value,
        name: maxInput.getAttribute('name') || '',
        paramName: maxInput.dataset.priceParamName || '',
        disabled: maxInput.disabled
      },
      labels: {
        min: minLabel ? minLabel.textContent : '',
        max: maxLabel ? maxLabel.textContent : ''
      },
      block: {
        isDisabled: Boolean(priceBlock && priceBlock.classList.contains('filter-block--disabled')),
        isSingle: Boolean(priceBlock && priceBlock.classList.contains('filter-block--single-price'))
      }
    };
  }

  restorePriceUiState(panel, state) {
    if (!panel || !state) return;

    const minInput = panel.querySelector('[data-price-min]');
    const maxInput = panel.querySelector('[data-price-max]');
    const minLabel = panel.querySelector('[data-price-min-label]');
    const maxLabel = panel.querySelector('[data-price-max-label]');
    const priceBlock = minInput ? minInput.closest('.filter-block') : null;
    if (!minInput || !maxInput) return;

    minInput.min = state.min.min || '0';
    minInput.max = state.min.max || minInput.max;
    minInput.value = state.min.value || '';
    minInput.disabled = Boolean(state.min.disabled);
    minInput.dataset.priceParamName = state.min.paramName || minInput.dataset.priceParamName || '';
    if (state.min.name) {
      minInput.setAttribute('name', state.min.name);
    } else {
      minInput.removeAttribute('name');
    }

    maxInput.min = state.max.min || '0';
    maxInput.max = state.max.max || maxInput.max;
    maxInput.value = state.max.value || '';
    maxInput.disabled = Boolean(state.max.disabled);
    maxInput.dataset.priceParamName = state.max.paramName || maxInput.dataset.priceParamName || '';
    if (state.max.name) {
      maxInput.setAttribute('name', state.max.name);
    } else {
      maxInput.removeAttribute('name');
    }

    if (minLabel) minLabel.textContent = state.labels.min || minLabel.textContent;
    if (maxLabel) maxLabel.textContent = state.labels.max || maxLabel.textContent;

    if (priceBlock) {
      priceBlock.classList.toggle('filter-block--disabled', Boolean(state.block.isDisabled));
      priceBlock.classList.toggle('filter-block--single-price', Boolean(state.block.isSingle));
      priceBlock.setAttribute('aria-disabled', state.block.isDisabled || state.block.isSingle ? 'true' : 'false');
    }
  }

  queueAutoApply(context = {}) {
    if (!this.isAutoApplyEnabled() || !this.form) return;
    if (window.A24Theme?.shouldProtectEditorClicks?.()) return;

    this.clearPendingAutoApply();
    const delay = this.resolveAutoApplyDelay(context);
    this.pendingAutoApplyTimer = window.setTimeout(() => {
      this.pendingAutoApplyTimer = null;
      this.runAutoApply(context);
    }, delay);
  }

  resolveAutoApplyDelay(context = {}) {
    if (context.source === 'ps' && this.config.psInputRequirement === 'smart' && this.psRange?.hasSingleValue?.(context.block)) {
      return Math.max(this.config.debounceMs, this.config.psSecondFieldWaitMs);
    }

    return this.config.debounceMs;
  }

  runAutoApply(context = {}) {
    if (!this.isAutoApplyEnabled()) return;
    if (context.source === 'ps' && this.psRange?.shouldWaitForSecondField?.(context.block)) {
      this.psRange.showError(context.block, 'Warte auf das zweite PS-Feld. Fülle es aus oder verlasse das Feld, um den Bereich anzuwenden.', 'info');
      this.queueAutoApply(context);
      return;
    }

    this.applyFilters({ auto: true, source: context.source });
  }

  prepareFiltersForSubmit(context = {}) {
    this.syncPriceFilterParams();
    return this.psRange.applyAll(context);
  }

  applyFilters(context = {}) {
    if (!this.form) return;
    this.clearPendingAutoApply();
    this.clearPendingPreviewRefresh();

    if (!this.prepareFiltersForSubmit(context)) return;

    /* PS-Änderungen auf Suchseiten gehören auf die ungefilterte
     * Produktkollektion. Marken- und sonstige native Filter werden aus dem
     * Formular übernommen. */
    if (this.psRange.redirectChangedSearchPsToCollection()) return;

    this.closeFilters();
    this.form.requestSubmit();
  }

  isSearchPage() {
    return window.location.pathname.includes('/search');
  }

  attachSearchFilterResetHandlers() {
    if (!this.isSearchPage()) return;
    if (window.A24Theme?.shouldProtectEditorClicks?.()) return;

    this.querySelectorAll('.active-filter-chips a.active-filter-chip').forEach((chip) => {
      this.addListener(chip, 'click', (event) => {
        const href = chip.getAttribute('href');
        if (!href) return;

        const target = new URL(href, window.location.origin);
        const hasNativeFilters = Array.from(target.searchParams.keys()).some((name) => name.startsWith('filter.'));
        if (hasNativeFilters) return;

        event.preventDefault();
        const collectionUrl = new URL('/collections/all', window.location.origin);
        const sortBy = target.searchParams.get('sort_by');
        if (sortBy) collectionUrl.searchParams.set('sort_by', sortBy);
        window.location.assign(collectionUrl.toString());
      });
    });
  }

  sortActiveFilterChips() {
    const chipsRoot = this.querySelector('.active-filter-chips');
    if (!chipsRoot) return;

    const orderedKeys = Array.from(this.querySelectorAll('.filter-block[data-filter-key]'))
      .map((block) => block.dataset.filterKey)
      .filter(Boolean);

    Array.from(chipsRoot.children)
      .sort((left, right) => {
        const leftIndex = orderedKeys.indexOf(left.dataset.filterKey || '');
        const rightIndex = orderedKeys.indexOf(right.dataset.filterKey || '');
        return (leftIndex < 0 ? Number.MAX_SAFE_INTEGER : leftIndex) -
          (rightIndex < 0 ? Number.MAX_SAFE_INTEGER : rightIndex);
      })
      .forEach((chip) => chipsRoot.appendChild(chip));
  }

  attachSelectionHandlers() {
    this.querySelectorAll('[data-select-toggle]').forEach((element) => {
      if (element.closest('[data-ps-range-block]')) return;

      const input = element.querySelector('input');
      if (!input) return;

      element.classList.toggle('is-active', input.checked);

      this.addListener(element, 'click', (event) => {
        if (event.target && event.target.tagName === 'INPUT') return;
        event.preventDefault();
        if (input.disabled) return;
        input.checked = !input.checked;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });

      this.addListener(input, 'change', () => {
        this.cancelPendingPriceBoundsCalculations();
        element.classList.toggle('is-active', input.checked);
        this.applyPriceOnRequestVendorRule();
        this.applyVendorPriceBoundsFromDomCache();
        this.queueAutoApply({ source: 'selection', input });
        this.queuePreviewRefresh({ source: 'selection', input });
      });
    });
  }

  attachFilterPanelHandlers() {
    this.filterToggles.forEach((button) => {
      this.addListener(button, 'click', (event) => {
        event.preventDefault();
        this.openFilters();
      });
    });

    this.addListener(this.filterClose, 'click', (event) => {
      event.preventDefault();
      this.closeFilters();
    });

    this.addListener(this.filterOverlay, 'click', this.closeFilters);
    this.addListener(window, 'keyup', this.handleEscapeKey);
    this.addListener(window, 'resize', this.handleViewportChange);
    this.addListener(window, 'orientationchange', this.handleViewportChange);
    this.syncFilterToggleState(Boolean(this.filtersPanel && this.filtersPanel.classList.contains('is-open')));
  }

  handleEscapeKey(event) {
    if (event.key === 'Escape') this.closeFilters();
  }

  attachFilterBlockCollapseHandlers() {
    this.querySelectorAll('[data-filter-collapse-toggle]').forEach((button, index) => {
      const block = button.closest('.filter-block');
      const content = block ? block.querySelector('[data-filter-collapse-content]') : null;
      if (!block || !content) return;

      if (!content.id) {
        content.id = 'a24-filter-block-content-' + index;
      }

      button.setAttribute('aria-controls', content.id);
      this.setFilterBlockCollapsed(block, false);

      this.addListener(button, 'click', (event) => {
        event.preventDefault();
        this.setFilterBlockCollapsed(block, !block.classList.contains('is-collapsed'));
      });
    });
  }

  setFilterBlockCollapsed(block, isCollapsed) {
    const button = block.querySelector('[data-filter-collapse-toggle]');
    const content = block.querySelector('[data-filter-collapse-content]');
    if (!button || !content) return;

    block.classList.toggle('is-collapsed', isCollapsed);
    button.setAttribute('aria-expanded', isCollapsed ? 'false' : 'true');
    content.hidden = isCollapsed;
    content.setAttribute('aria-hidden', isCollapsed ? 'true' : 'false');
  }

  syncFilterToggleState(isOpen) {
    this.filterToggles.forEach((button) => {
      button.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    });
  }

  openFilters() {
    if (this.filtersPanel) this.filtersPanel.classList.add('is-open');
    if (this.filterOverlay) this.filterOverlay.classList.add('is-active');
    document.body.classList.add('filters-open');
    window.dispatchEvent(new Event('scroll'));
    this.pinHeaderForMobileFilters(true);
    this.syncFilterToggleState(true);
  }

  closeFilters() {
    if (this.filtersPanel) this.filtersPanel.classList.remove('is-open');
    if (this.filterOverlay) this.filterOverlay.classList.remove('is-active');
    document.body.classList.remove('filters-open');
    window.dispatchEvent(new Event('scroll'));
    this.pinHeaderForMobileFilters(false);
    this.syncFilterToggleState(false);
  }

  handleViewportChange() {
    const isOpen = document.body.classList.contains('filters-open');

    if (isOpen && window.innerWidth > 900) {
      this.closeFilters();
      return;
    }

    this.pinHeaderForMobileFilters(isOpen);
  }

  pinHeaderForMobileFilters(shouldPin) {
    const header = document.getElementById('shopify-section-header');
    if (!header) return;

    if (shouldPin && window.innerWidth <= 749) {
      header.style.setProperty('position', 'fixed', 'important');
      header.style.setProperty('top', '0px', 'important');
      header.style.setProperty('left', '0px');
      header.style.setProperty('right', '0px');
      header.style.setProperty('width', '100%');
      return;
    }

    header.style.removeProperty('position');
    header.style.removeProperty('top');
    header.style.removeProperty('left');
    header.style.removeProperty('right');
    header.style.removeProperty('width');
  }

  isPriceOnRequestVendorSelection() {
    return this.shouldHidePriceForCurrentVendorContext();
  }

  applyPriceOnRequestVendorRule() {
    const priceBlock = this.minRange ? this.minRange.closest('.filter-block') : null;
    const shouldHidePrice = this.isPriceOnRequestVendorSelection();

    if (shouldHidePrice && this.minRange && this.maxRange) {
      [this.minRange, this.maxRange].forEach((input) => {
        input.min = '0';
        input.max = '0';
        input.value = '0';
      });

      if (this.minLabel) this.minLabel.textContent = this.formatMoney(0);
      if (this.maxLabel) this.maxLabel.textContent = this.formatMoney(0);
    }

    if (priceBlock) {
      priceBlock.classList.toggle('filter-block--disabled', shouldHidePrice);
      priceBlock.setAttribute('aria-disabled', shouldHidePrice ? 'true' : 'false');
      priceBlock.removeAttribute('aria-hidden');

      const existingNotice = priceBlock.querySelector('[data-price-disabled-notice]');
      if (shouldHidePrice && !existingNotice) {
        const notice = document.createElement('p');
        notice.className = 'filter-block__disabled-note';
        notice.setAttribute('data-price-disabled-notice', '');
        notice.textContent = 'Preisfilter für die aktuelle Herstellerauswahl nicht verfügbar.';
        const content = priceBlock.querySelector('[data-filter-collapse-content]');
        if (content) content.appendChild(notice);
      } else if (!shouldHidePrice && existingNotice) {
        existingNotice.remove();
      }
    }

    [this.minRange, this.maxRange].forEach((input) => {
      if (!input) return;
      input.disabled = shouldHidePrice;

      if (shouldHidePrice) {
        input.removeAttribute('name');
      }
    });

    this.hidePriceActiveChip(shouldHidePrice);
  }

  hidePriceActiveChip(shouldHidePrice) {
    const chipsRoot = this.querySelector('.active-filter-chips');
    if (!chipsRoot) return;

    chipsRoot.querySelectorAll('.active-filter-chip').forEach((chip) => {
      const filterKey = String(chip.dataset.filterKey || '').trim();
      const chipLabel = String(chip.textContent || '').trim().toLowerCase();
      const isPriceChip = filterKey.indexOf('filter.v.price.') === 0 || chipLabel.indexOf('preis') === 0;

      if (!isPriceChip) return;

      chip.hidden = shouldHidePrice;
      chip.setAttribute('aria-hidden', shouldHidePrice ? 'true' : 'false');
    });
  }

  updatePriceSingleValueState() {
    const priceBlock = this.minRange ? this.minRange.closest('[data-price-filter-block], .filter-block') : null;
    if (!priceBlock || !this.minRange || !this.maxRange) return;

    if (priceBlock.hidden) {
      priceBlock.classList.remove('filter-block--single-price');
      priceBlock.removeAttribute('data-price-single-value');
      priceBlock.querySelector('[data-price-single-notice]')?.remove();
      return;
    }

    const minLimit = this.parseNumber(this.minRange.min);
    const maxLimit = this.parseNumber(this.maxRange.max);
    const hasSinglePrice = Number.isFinite(minLimit) && Number.isFinite(maxLimit) && minLimit >= maxLimit;

    priceBlock.classList.toggle('filter-block--single-price', hasSinglePrice);
    priceBlock.toggleAttribute('data-price-single-value', hasSinglePrice);
    priceBlock.setAttribute('aria-disabled', hasSinglePrice ? 'true' : 'false');

    [this.minRange, this.maxRange].forEach((input) => {
      if (!input) return;
      input.setAttribute('aria-disabled', hasSinglePrice ? 'true' : 'false');
      input.toggleAttribute('data-price-single-input', hasSinglePrice);
      if (hasSinglePrice) {
        input.tabIndex = -1;
      } else {
        input.removeAttribute('tabindex');
      }
    });

    priceBlock.querySelector('[data-price-single-notice]')?.remove();
  }

  handleRangeChange(event) {
    if (!this.minRange || !this.maxRange) return;

    let min = this.parseNumber(this.minRange.value);
    let max = this.parseNumber(this.maxRange.value);

    if (!Number.isFinite(min)) min = 0;
    if (!Number.isFinite(max)) max = 0;

    if (min > max) {
      const previousMin = min;
      min = max;
      max = previousMin;
      this.minRange.value = String(min);
      this.maxRange.value = String(max);
    }

    if (this.minLabel) this.minLabel.textContent = this.formatMoney(min);
    if (this.maxLabel) this.maxLabel.textContent = this.formatMoney(max);
    this.syncPriceFilterParams();
    this.updatePriceSingleValueState();

    if (event?.type) {
      this.queueAutoApply({ source: 'price', input: event.currentTarget });
    }
  }

  handleApply(event) {
    event.preventDefault();
    this.applyFilters({ manual: true, source: 'button' });
  }

  handleFormSubmit(event) {
    this.clearPendingAutoApply();
    this.clearPendingPreviewRefresh();

    if (!this.prepareFiltersForSubmit({ manual: true, source: 'submit' })) {
      event.preventDefault();
      return;
    }

    if (this.psRange.redirectChangedSearchPsToCollection()) {
      event.preventDefault();
      return;
    }

    this.form.querySelectorAll('input[name="page"]').forEach((input) => input.remove());
    window.A24SearchRouting?.showLoading?.('Filter werden geladen…');
  }

  initializePriceInputs() {
    [this.minRange, this.maxRange].forEach((input) => {
      if (!input) return;
      if (!input.dataset.priceParamName) {
        input.dataset.priceParamName = input.getAttribute('name') || '';
      }
    });

    this.syncPriceFilterParams();
  }

  syncPriceFilterParams() {
    if (!this.minRange || !this.maxRange) return;

    if (this.isPriceOnRequestVendorSelection()) {
      this.applyPriceOnRequestVendorRule();
      return;
    }

    const min = this.parseNumber(this.minRange.value);
    const max = this.parseNumber(this.maxRange.value);
    const minLimit = this.parseNumber(this.minRange.min);
    const maxLimit = this.parseNumber(this.maxRange.max);

    const minStep = this.parseNumber(this.minRange.step);
    const maxStep = this.parseNumber(this.maxRange.step);
    const minTolerance = Number.isFinite(minStep) && minStep > 0 ? minStep : 1;
    const maxTolerance = Number.isFinite(maxStep) && maxStep > 0 ? maxStep : 1;

    const minIsDefault = Number.isFinite(min) && Number.isFinite(minLimit) && min <= (minLimit + minTolerance);
    const maxIsDefault = Number.isFinite(max) && Number.isFinite(maxLimit) && max >= (maxLimit - maxTolerance);

    this.updatePriceInputName(this.minRange, minIsDefault);
    this.updatePriceInputName(this.maxRange, maxIsDefault);
  }

  cancelPendingPriceBoundsCalculations() {
    // Bei jeder Filter-Änderung alte Requests hart invalidieren, damit
    // verspätete Antworten keine neuen Bounds überschreiben.
    if (this.priceBoundsAbortController) this.priceBoundsAbortController.abort();
    this.priceBoundsAbortController = null;
    this.priceBoundsRequestId += 1;

    if (this.vendorPriceBoundsAbortController) this.vendorPriceBoundsAbortController.abort();
    this.vendorPriceBoundsAbortController = null;
    this.vendorPriceBoundsWarmupRequestId += 1;
  }

  updatePriceInputName(input, removeName) {
    if (!input) return;

    const paramName = input.dataset.priceParamName || '';
    if (removeName) {
      input.removeAttribute('name');
      return;
    }

    if (paramName) input.setAttribute('name', paramName);
  }

  /**
   * Shopify's range_max can retain the collection-wide maximum after a
   * vendor/metafield filter is active. Build the visible price limits from
   * every pagination page of the already filtered result set instead.
   *
   * The current query string is retained; only `page` is changed. Therefore
   * the fetched product cards always represent exactly the active native
   * Search & Discovery filter state.
   */
  async refreshPriceBoundsFromFilteredProducts(baseUrl = null) {
    if (!this.minRange || !this.maxRange || this.isPriceOnRequestVendorSelection()) return;

    const resolvedBaseUrl = this.resolvePriceBoundsContextUrl(baseUrl);
    const requestId = ++this.priceBoundsRequestId;

    if (this.priceBoundsAbortController) this.priceBoundsAbortController.abort();
    this.priceBoundsAbortController = new AbortController();

    try {
      const hasPreviewContext = Boolean(baseUrl);
      let prices = [];

      if (hasPreviewContext) {
        const firstPageData = await this.fetchFilteredProductPageData(
          1,
          this.priceBoundsAbortController.signal,
          resolvedBaseUrl
        );

        prices = prices.concat(firstPageData.prices);

        // Kein Zwischen-Apply aus Seite 1: erst das finale Ergebnis aller
        // Seiten setzen, um sichtbare Sprünge zu vermeiden.
        const totalPages = Math.max(1, Number.parseInt(firstPageData.totalPages || '1', 10) || 1);
        if (totalPages > 1) {
          const remainingPrices = await this.fetchFilteredProductPrices(
            totalPages,
            this.priceBoundsAbortController.signal,
            resolvedBaseUrl,
            2
          );
          prices = prices.concat(remainingPrices);
        }
      } else {
        const grid = document.querySelector('.product-grid[data-total-pages]');
        if (!grid) return;

        const totalPages = Math.max(1, Number.parseInt(grid.dataset.totalPages || '1', 10) || 1);
        prices = await this.fetchFilteredProductPrices(
          totalPages,
          this.priceBoundsAbortController.signal,
          resolvedBaseUrl
        );
      }

      if (requestId !== this.priceBoundsRequestId || !prices.length) return;

      const minimum = Math.min(...prices);
      const maximum = Math.max(...prices);

      if (!Number.isFinite(minimum) || !Number.isFinite(maximum)) return;

      this.applyFilteredPriceBounds(minimum, maximum);
    } catch (error) {
      if (error && error.name === 'AbortError') return;
      // Keep Shopify's server-rendered range as a safe fallback when a
      // pagination request cannot be completed.
      console.warn('Preisgrenzen konnten nicht aus den gefilterten Produkten ermittelt werden.', error);
    }
  }

  async fetchFilteredProductPrices(totalPages, signal, baseUrl, startPage = 1) {
    const pageNumbers = Array.from(
      { length: Math.max(0, totalPages - startPage + 1) },
      (_item, index) => index + startPage
    );
    const prices = [];

    // Small batches avoid firing dozens of requests at once on large result
    // sets while still keeping the initial filter panel responsive.
    const batchSize = 4;

    for (let start = 0; start < pageNumbers.length; start += batchSize) {
      const batch = pageNumbers.slice(start, start + batchSize);
      const batchResults = await Promise.all(
        batch.map((pageNumber) => this.fetchFilteredProductPricesForPage(pageNumber, signal, baseUrl))
      );

      batchResults.forEach((pagePrices) => prices.push(...pagePrices));
    }

    return prices;
  }

  async fetchFilteredProductPricesForPage(pageNumber, signal, baseUrl) {
    const pageData = await this.fetchFilteredProductPageData(pageNumber, signal, baseUrl);
    return pageData.prices;
  }

  async fetchFilteredProductPageData(pageNumber, signal, baseUrl) {
    const url = new URL((baseUrl || window.location.href).toString(), window.location.origin);
    url.searchParams.delete('section_id');
    if (pageNumber <= 1) {
      url.searchParams.delete('page');
    } else {
      url.searchParams.set('page', String(pageNumber));
    }

    const response = await fetch(url.toString(), {
      credentials: 'same-origin',
      signal,
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    });

    if (!response.ok) {
      throw new Error('Preisabfrage fehlgeschlagen: HTTP ' + response.status);
    }

    const html = await response.text();
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    const productGrid = documentFragment.querySelector('.collection-section .product-grid') ||
      documentFragment.querySelector('.product-grid');

    if (!productGrid) {
      return {
        prices: [],
        totalPages: 1
      };
    }

    const totalPages = Math.max(1, Number.parseInt(productGrid.dataset.totalPages || '1', 10) || 1);

    const cards = productGrid.querySelectorAll('.product-card');
    const prices = this.getPriceValuesFromProductCards(cards);
    const vendorBounds = Object.create(null);
    Array.from(cards).forEach((card) => {
      this.mergeVendorBoundsMap(vendorBounds, this.extractVendorBoundsFromProductCard(card));
    });

    return {
      prices,
      totalPages,
      vendorBounds
    };
  }

  applyFilteredPriceBounds(minimum, maximum) {
    const hasExplicitPriceFilter = this.hasExplicitPriceFilterInUrl();
    const normalizedMinimum = Math.max(0, minimum);
    const normalizedMaximum = Math.max(normalizedMinimum, maximum);

    [this.minRange, this.maxRange].forEach((input) => {
      input.min = String(normalizedMinimum);
      input.max = String(normalizedMaximum);
    });

    if (!hasExplicitPriceFilter) {
      this.minRange.value = String(normalizedMinimum);
      this.maxRange.value = String(normalizedMaximum);
    } else {
      const selectedMinimum = Math.max(normalizedMinimum, this.parseNumber(this.minRange.value));
      const selectedMaximum = Math.min(normalizedMaximum, this.parseNumber(this.maxRange.value));

      this.minRange.value = String(Number.isFinite(selectedMinimum) ? selectedMinimum : normalizedMinimum);
      this.maxRange.value = String(Number.isFinite(selectedMaximum) ? selectedMaximum : normalizedMaximum);
    }

    this.handleRangeChange();
  }

  hasExplicitPriceFilterInUrl() {
    const params = new URLSearchParams(window.location.search);

    return params.has(this.minRange.dataset.priceParamName || 'filter.v.price.gte') ||
      params.has(this.maxRange.dataset.priceParamName || 'filter.v.price.lte');
  }

  parseMoney(value) {
    const raw = String(value || '').replace(/\s+/g, '').replace(/[^\d,.-]/g, '');
    if (!raw) return Number.NaN;

    const lastComma = raw.lastIndexOf(',');
    const lastDot = raw.lastIndexOf('.');
    let normalized = raw;

    if (lastComma > lastDot) {
      normalized = raw.replace(/\./g, '').replace(',', '.');
    } else if (lastDot > lastComma) {
      normalized = raw.replace(/,/g, '');
    } else {
      normalized = raw.replace(',', '.');
    }

    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : Number.NaN;
  }

  parseNumber(value) {
    if (value === null || value === undefined) return Number.NaN;

    const normalized = String(value)
      .trim()
      .replace(/\s+/g, '')
      .replace(',', '.');

    if (!normalized) return Number.NaN;

    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : Number.NaN;
  }

  formatInputNumber(value) {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return '';

    return Number.isInteger(numeric) ? String(numeric) : String(numeric).replace('.', ',');
  }

  formatMoney(amount) {
    return this.currencySymbol + Number(amount || 0).toLocaleString('de-DE', {
      maximumFractionDigits: 0
    });
  }
}

if (!customElements.get('collection-filters')) {
  customElements.define('collection-filters', CollectionFilters);
}
